import React, { useState, useEffect } from 'react';
import { part1Questions, part2Questions, part3Questions } from './App';

export default function AdminDashboard({ onBack, adminKey }) {
  const [submissions, setSubmissions] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const adminHeaders = { 'X-Admin-Key': adminKey };

  useEffect(() => {
    fetchSubmissions();
    window.scrollTo(0, 0);
  }, []);

  const fetchSubmissions = async () => {
    try {
      setLoading(true);
      const response = await fetch('/api/submissions', { headers: adminHeaders });
      if (response.status === 401) { setError('Not authorised.'); return; }
      const data = await response.json();
      setSubmissions(data || []);
      setError(null);
    } catch (err) {
      setError('Failed to load submissions');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetails = async (id) => {
    try {
      const response = await fetch(`/api/submissions/${id}`, { headers: adminHeaders });
      const data = await response.json();
      setSelectedSubmission(data);
      setSelectedId(id);
    } catch (err) {
      console.error('Failed to load details:', err);
    }
  };

  const handleExport = async () => {
    const response = await fetch('/api/export', { headers: adminHeaders });
    if (!response.ok) return;
    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'submissions.csv';
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const handleViewReport = async (id) => {
    const response = await fetch(`/api/submissions/${id}/report`, { headers: adminHeaders });
    if (!response.ok) return;
    const html = await response.text();
    const w = window.open('', '_blank');
    w.document.write(html);
    w.document.close();
  };

  const handleCloseDetails = () => {
    setSelectedSubmission(null);
    setSelectedId(null);
  };

  const getPart1QuestionText = (questionId) => {
    const q = part1Questions.find(q => q.id === questionId);
    return q ? q.prompt : questionId;
  };

  const getPart2QuestionText = (questionId) => {
    const q = part2Questions.find(q => q.id === questionId);
    return q ? q.text : questionId;
  };

  const getPart3QuestionText = (questionId) => {
    const q = part3Questions.find(q => q.id === parseInt(questionId));
    return q ? q.prompt : `Question ${questionId}`;
  };

  return (
    <div className="admin-dashboard">
      <div className="admin-header">
        <div className="admin-header-content">
          <h1>Admin Dashboard</h1>
          <p>Assessment Submissions</p>
        </div>
        <button className="btn-back" onClick={onBack}>← Back</button>
      </div>

      {selectedSubmission ? (
        <div className="admin-detail-view">
          <div style={{display: 'flex', gap: '12px', marginBottom: '20px'}}>
            <button className="btn-back-detail" onClick={handleCloseDetails}>← Back to List</button>
            <button className="btn-primary" onClick={() => handleViewReport(selectedId)} style={{padding: '10px 16px', fontSize: '14px'}}>View Full Report</button>
          </div>
          
          <div className="submission-detail">
            <h2>Submission Details</h2>
            
            <div className="detail-section">
              <h3>User Information</h3>
              <div className="detail-grid">
                <div className="detail-item">
                  <label>Name</label>
                  <p>{selectedSubmission.userData.name || 'Not provided'}</p>
                </div>
                <div className="detail-item">
                  <label>Email</label>
                  <p>{selectedSubmission.userData.email}</p>
                </div>
                <div className="detail-item">
                  <label>Organisation</label>
                  <p>{selectedSubmission.userData.organisation || 'Not provided'}</p>
                </div>
                <div className="detail-item">
                  <label>Date Submitted</label>
                  <p>{new Date(selectedSubmission.timestamp).toLocaleString()}</p>
                </div>
              </div>
            </div>

            <div className="detail-section">
              <h3>Transition Context</h3>
              <div className="detail-grid">
                <div className="detail-item">
                  <label>Complexity Score</label>
                  <p>{selectedSubmission.results.complexityScore}/18</p>
                </div>
                <div className="detail-item">
                  <label>Complexity Level</label>
                  <p className={`badge badge-${selectedSubmission.results.complexityLevel}`}>
                    {selectedSubmission.results.complexityLabel}
                  </p>
                </div>
              </div>
            </div>

            <div className="detail-section">
              <h3>Readiness Profile</h3>
              <div className="detail-grid">
                <div className="detail-item">
                  <label>Foundational Knowledge</label>
                  <div className="readiness-bar-detail">
                    <div className="bar-fill" style={{width: `${(selectedSubmission.results.foundationalScore / 20) * 100}%`}}></div>
                  </div>
                  <p className="score-text">{selectedSubmission.results.foundationalScore}/20 ({selectedSubmission.results.foundationalLevel})</p>
                </div>
                <div className="detail-item">
                  <label>Socio-Political Awareness</label>
                  <div className="readiness-bar-detail">
                    <div className="bar-fill" style={{width: `${(selectedSubmission.results.sociopoliticalScore / 20) * 100}%`}}></div>
                  </div>
                  <p className="score-text">{selectedSubmission.results.sociopoliticalScore}/20 ({selectedSubmission.results.sociopoliticalLevel})</p>
                </div>
                <div className="detail-item">
                  <label>Strategic Clarity</label>
                  <div className="readiness-bar-detail">
                    <div className="bar-fill" style={{width: `${(selectedSubmission.results.strategicScore / 20) * 100}%`}}></div>
                  </div>
                  <p className="score-text">{selectedSubmission.results.strategicScore}/20 ({selectedSubmission.results.strategicLevel})</p>
                </div>
              </div>
              <div className="detail-item">
                <label>Overall Readiness</label>
                <p>{selectedSubmission.results.overallReadinessScore}/20 ({selectedSubmission.results.overallReadinessLevel})</p>
              </div>
            </div>

            <div className="detail-section">
              <h3>Transition Approach Pattern</h3>
              <div className="detail-grid">
                <div className="detail-item">
                  <label>Cycle Scores</label>
                  <ul className="cycle-list">
                    <li>Explore: {selectedSubmission.results.exploreScore}</li>
                    <li>Analyse: {selectedSubmission.results.analyseScore}</li>
                    <li>Decide: {selectedSubmission.results.decideScore}</li>
                    <li>Act: {selectedSubmission.results.actScore}</li>
                  </ul>
                </div>
                <div className="detail-item">
                  <label>Pattern</label>
                  <p>Dwells in: <strong>{selectedSubmission.results.dwellsIn}</strong></p>
                  <p>Skips: <strong>{selectedSubmission.results.skips}</strong></p>
                </div>
              </div>
            </div>

            <div className="detail-section">
              <h3>Part 1: Transition Context - Responses</h3>
              <div className="simple-answers">
                {Object.entries(selectedSubmission.part1Answers || {}).map(([key, answer]) => {
                  const answerText = typeof answer === 'object' ? (answer.text || answer.value) : answer;
                  return (
                    <div key={key} className="simple-answer-item">
                      <p><strong>{getPart1QuestionText(key)}</strong></p>
                      <p>{answerText}</p>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="detail-section">
              <h3>Part 2: Readiness Profile - Responses</h3>
              <div className="simple-answers">
                {Object.entries(selectedSubmission.part2Answers || {}).map(([key, score]) => (
                  <div key={key} className="simple-answer-item">
                    <p><strong>{getPart2QuestionText(key)}</strong></p>
                    <p>Score: <strong>{score}/4</strong></p>
                  </div>
                ))}
              </div>
            </div>

            <div className="detail-section">
              <h3>Part 3: Transition Approach - Rankings</h3>
              <div className="simple-answers">
                {Object.entries(selectedSubmission.part3Answers || {}).map(([key, rankings]) => (
                  <div key={key} className="simple-answer-item">
                    <p><strong>{getPart3QuestionText(key)}</strong></p>
                    <p>
                      {Object.entries(rankings).sort((a, b) => a[1] - b[1]).map(([stage, rank]) => 
                        `#${rank} ${stage.charAt(0).toUpperCase() + stage.slice(1)}`
                      ).join(', ')}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="admin-content">
          <div className="admin-controls">
            <button className="btn-primary" onClick={handleExport}>📥 Download CSV</button>
            <button className="btn-secondary" onClick={fetchSubmissions}>🔄 Refresh</button>
          </div>

          {loading && <p className="loading-text">Loading submissions...</p>}
          {error && <p className="error-text">{error}</p>}

          {!loading && submissions.length === 0 && (
            <p className="empty-text">No submissions yet</p>
          )}

          {!loading && submissions.length > 0 && (
            <div className="submissions-list">
              <p className="submission-count">{submissions.length} submission{submissions.length !== 1 ? 's' : ''}</p>
              <div className="table-wrapper">
                <table className="submissions-table">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Name</th>
                      <th>Email</th>
                      <th>Organisation</th>
                      <th>Complexity</th>
                      <th>Readiness</th>
                      <th>Pattern</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {submissions.map(sub => (
                      <tr key={sub.id}>
                        <td className="date-col">{new Date(sub.timestamp).toLocaleDateString()}</td>
                        <td>{sub.name || '—'}</td>
                        <td className="email-col">{sub.email}</td>
                        <td>{sub.organisation || '—'}</td>
                        <td>
                          <span className={`badge badge-${sub.complexityLabel.toLowerCase().includes('low') ? 'low' : sub.complexityLabel.toLowerCase().includes('very') ? 'very_high' : sub.complexityLabel.toLowerCase().includes('moderate') ? 'moderate' : 'high'}`}>
                            {sub.complexityLabel}
                          </span>
                        </td>
                        <td>{sub.overallReadinessLevel}</td>
                        <td className="pattern-col">{sub.dwellsIn} / {sub.skips}</td>
                        <td>
                          <div style={{display: 'flex', gap: '6px'}}>
                            <button className="btn-view" onClick={() => handleViewDetails(sub.id)}>Data</button>
                            <button className="btn-view" onClick={() => handleViewReport(sub.id)}>Report</button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
